import { useEffect, useMemo, useRef, useState } from 'react';
import {
	Box,
	Button,
	Divider,
	Flex,
	FormControl,
	FormLabel,
	HStack,
	Icon,
	IconButton,
	Input,
	SimpleGrid,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useColorModeValue,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import { ViewIcon } from '@chakra-ui/icons';
import { MdCloudUpload, MdDownload, MdEdit, MdFilterList, MdRefresh } from 'react-icons/md';
import { useQuery } from 'react-query';
import Card from 'components/card/Card';
import {
	ApiProductsMasterBulkUploadFile,
	ApiProductsMasterList,
	mapProductsMasterRow,
	PRODUCTS_MASTER_TEMPLATE_URL,
} from 'api/masters/productsList';
import { ProductMasterRow } from './types';
import ProductViewUnitsModal from './ProductViewUnitsModal';
import ProductEditModal from './ProductEditModal';

const PAGE_SIZE = 10;
const PRODUCTS_MASTER_BULK_ACCEPT =
	'.xlsx,.xlsm,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

export default function ProductsMasterView() {
	const [filterSearch, setFilterSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [page, setPage] = useState(0);
	const [bulkUploading, setBulkUploading] = useState(false);
	const toast = useToast();
	const fileRef = useRef<HTMLInputElement>(null);

	const viewDisc = useDisclosure();
	const editDisc = useDisclosure();
	const [activeProduct, setActiveProduct] = useState<ProductMasterRow | null>(null);

	useEffect(() => {
		const timer = window.setTimeout(() => setDebouncedSearch(filterSearch.trim()), 350);
		return () => window.clearTimeout(timer);
	}, [filterSearch]);

	const listQuery = useQuery(
		['masters-products-list', page, debouncedSearch],
		async () => {
			const response = await ApiProductsMasterList({
				page: page + 1,
				page_size: PAGE_SIZE,
				search: debouncedSearch || undefined,
			});
			return response.data;
		},
		{ keepPreviousData: true },
	);

	const apiProducts = useMemo(
		() => (listQuery.data?.results ?? []).map(mapProductsMasterRow),
		[listQuery.data?.results],
	);

	const products = apiProducts;

	const subText = useColorModeValue('secondaryGray.600', 'secondaryGray.400');
	const rowAlt = useColorModeValue('white', 'navy.900');
	const filterShellBg = useColorModeValue(
		'linear-gradient(165deg, rgba(14, 165, 233, 0.06) 0%, rgba(15, 118, 110, 0.04) 55%, rgba(248, 250, 252, 0.9) 100%)',
		'linear-gradient(165deg, rgba(14, 165, 233, 0.12) 0%, rgba(15, 118, 110, 0.08) 100%)',
	);
	const filterShellBorder = useColorModeValue('secondaryGray.200', 'whiteAlpha.200');
	const filterAccent = useColorModeValue('teal.500', 'teal.300');
	const selectFieldBg = useColorModeValue('white', 'navy.800');

	useEffect(() => {
		if (listQuery.isError) {
			toast({
				title: 'No se pudo cargar el catálogo',
				description: 'Verifica la conexión con datamart y vuelve a intentar.',
				status: 'error',
				duration: 5000,
			});
		}
	}, [listQuery.isError, toast]);

	const pageCount = Math.max(1, listQuery.data?.total_pages ?? 1);
	const safePage = Math.min(page, pageCount - 1);
	const pageRows = products;
	const totalCount = listQuery.data?.count ?? 0;
	const hasActiveFilters = Boolean(debouncedSearch);

	const openView = (p: ProductMasterRow) => {
		setActiveProduct(p);
		viewDisc.onOpen();
	};

	const openEdit = (p: ProductMasterRow) => {
		setActiveProduct(p);
		editDisc.onOpen();
	};

	const onEditSave = (_updated: ProductMasterRow) => {
		listQuery.refetch();
		toast({ title: 'Cambios guardados', status: 'success', duration: 2500 });
	};

	const onBulkFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file) return;

		const lowerName = file.name.toLowerCase();
		if (!lowerName.endsWith('.xlsx') && !lowerName.endsWith('.xlsm') && !lowerName.endsWith('.xls')) {
			toast({
				title: 'Formato no soportado',
				description: 'Usá la plantilla Excel (.xlsx) de productos.',
				status: 'warning',
				duration: 5000,
			});
			return;
		}

		setBulkUploading(true);
		try {
			const response = await ApiProductsMasterBulkUploadFile(file);
			const data = response.data;
			const errors = data.errors ?? [];
			if (errors.length) {
				const MAX_TOAST_ERRORS = 6;
				const lines = errors
					.slice(0, MAX_TOAST_ERRORS)
					.map(
						(err) =>
							`Fila ${err.row}${err.codigo ? ` (${err.codigo})` : ''}: ${err.message}`,
					);
				if (errors.length > MAX_TOAST_ERRORS) {
					lines.push(`…y ${errors.length - MAX_TOAST_ERRORS} errores más.`);
				}
				toast({
					title:
						errors.length === 1
							? '1 fila con error en la carga'
							: `${errors.length} filas con error en la carga`,
					description: <Box whiteSpace="pre-line">{lines.join('\n')}</Box>,
					status: 'warning',
					duration: null,
					isClosable: true,
				});
			} else {
				toast({
					title: 'Carga masiva procesada',
					description: `${data.created} creados, ${data.updated} actualizados.`,
					status: 'success',
					duration: 6000,
				});
			}
			setPage(0);
			listQuery.refetch();
		} catch (err) {
			const apiMessage =
				(err as { response?: { data?: { message?: string; detail?: string } } })?.response
					?.data?.message ??
				(err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
			toast({
				title: 'No se pudo procesar la carga masiva',
				description:
					apiMessage ?? (err instanceof Error ? err.message : 'Error inesperado'),
				status: 'error',
				duration: 5000,
			});
		} finally {
			setBulkUploading(false);
		}
	};

	return (
		<Box pt={{ base: '120px', md: '75px' }} px={{ base: '20px', md: '30px' }}>
			<input
				ref={fileRef}
				type="file"
				accept={PRODUCTS_MASTER_BULK_ACCEPT}
				hidden
				onChange={onBulkFile}
			/>
			<Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
				<Box px="25px" mb="20px">
					<Flex
						mb={4}
						justify="space-between"
						align={{ base: 'stretch', md: 'center' }}
						direction={{ base: 'column', md: 'row' }}
						gap={3}>
						<Text color="secondaryGray.900" fontSize="xl" fontWeight="700" letterSpacing="-0.02em">
							Productos
						</Text>
						<HStack spacing={2} flexWrap="wrap">
							<Button
								size="sm"
								variant="ghost"
								leftIcon={<MdRefresh />}
								isLoading={listQuery.isFetching}
								onClick={() => listQuery.refetch()}>
								Actualizar
							</Button>
							<Button
								as="a"
								href={PRODUCTS_MASTER_TEMPLATE_URL}
								download
								size="sm"
								colorScheme="blue"
								variant="outline"
								leftIcon={<MdDownload />}>
								Descargar plantilla
							</Button>
							<Button
								size="sm"
								colorScheme="teal"
								variant="outline"
								leftIcon={<MdCloudUpload />}
								isLoading={bulkUploading}
								alignSelf={{ base: 'stretch', md: 'auto' }}
								onClick={() => fileRef.current?.click()}>
								Carga masiva (Excel)
							</Button>
						</HStack>
					</Flex>
					<Divider mb={4} />
					<Box
						position="relative"
						overflow="hidden"
						borderRadius="xl"
						borderWidth="1px"
						borderColor={filterShellBorder}
						bg={filterShellBg}
						px={{ base: 4, md: 5 }}
						py={5}
						boxShadow="sm">
						<Box
							position="absolute"
							left={0}
							top={0}
							bottom={0}
							w="4px"
							bgGradient="linear(to-b, teal.400, cyan.500)"
							aria-hidden
						/>
						<Flex align="center" gap={2} mb={4} pl={1}>
							<Icon as={MdFilterList} color={filterAccent} boxSize="18px" />
							<Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="0.14em" color={subText}>
								Filtros del catálogo
							</Text>
						</Flex>
						<SimpleGrid columns={1} spacingY={4}>
							<FormControl>
								<FormLabel fontSize="xs" fontWeight="700" mb={1.5} color={subText}>
									Buscar…
								</FormLabel>
								<Input
									placeholder="Código, descripción…"
									size="sm"
									bg={selectFieldBg}
									value={filterSearch}
									onChange={(ev) => {
										setFilterSearch(ev.target.value);
										setPage(0);
									}}
								/>
							</FormControl>
						</SimpleGrid>
					</Box>
				</Box>
				<Box overflowX="auto" px="25px" pb={6}>
					<Table variant="simple" size="sm">
						<Thead>
							<Tr>
								<Th>Código</Th>
								<Th>Descripción</Th>
								<Th>Categoría</Th>
								<Th>Línea</Th>
								<Th>Sublínea</Th>
								<Th>Tipo de material</Th>
								<Th w="100px">Acciones</Th>
							</Tr>
						</Thead>
						<Tbody>
							{listQuery.isLoading && pageRows.length === 0 ? (
								<Tr>
									<Td colSpan={7}>
										<Text fontSize="sm" color={subText} py={6} textAlign="center">
											Cargando productos desde datamart…
										</Text>
									</Td>
								</Tr>
							) : null}
							{!listQuery.isLoading && pageRows.length === 0 ? (
								<Tr>
									<Td colSpan={7}>
										<Text fontSize="sm" color={subText} py={6} textAlign="center">
											{listQuery.isError
												? 'No se pudo cargar el catálogo. Verifica tu sesión, permisos o la conexión con datamart.'
												: hasActiveFilters
													? 'No hay productos para los filtros seleccionados.'
													: 'No hay productos disponibles en el catálogo.'}
										</Text>
									</Td>
								</Tr>
							) : null}
							{pageRows.map((product, idx) => {
								const bgRow = idx % 2 === 0 ? rowAlt : 'transparent';
								return (
									<Tr key={product.id} bg={bgRow}>
										<Td fontWeight="600">{product.codigo}</Td>
										<Td>{product.descripcion}</Td>
										<Td>{product.categoria}</Td>
										<Td>{product.linea}</Td>
										<Td>{product.sublinea}</Td>
										<Td>{product.tipoMaterial || '—'}</Td>
										<Td>
											<HStack spacing={1}>
												<IconButton
													aria-label="Editar"
													icon={<MdEdit />}
													size="sm"
													variant="ghost"
													onClick={() => openEdit(product)}
												/>
												<IconButton
													aria-label="Visualizar"
													icon={<ViewIcon />}
													size="sm"
													variant="ghost"
													onClick={() => openView(product)}
												/>
											</HStack>
										</Td>
									</Tr>
								);
							})}
						</Tbody>
					</Table>
					<Flex mt={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
						<Text fontSize="sm" color={subText}>
							Mostrando {pageRows.length} de {totalCount} productos
						</Text>
						<HStack>
							<Button
								size="sm"
								variant="outline"
								isDisabled={safePage <= 0 || listQuery.isFetching}
								onClick={() => setPage((p) => Math.max(0, p - 1))}>
								Anterior
							</Button>
							<Text fontSize="sm">
								Página {safePage + 1} / {pageCount}
							</Text>
							<Button
								size="sm"
								variant="outline"
								isDisabled={safePage >= pageCount - 1 || listQuery.isFetching}
								onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}>
								Siguiente
							</Button>
						</HStack>
					</Flex>
				</Box>
			</Card>

			<ProductViewUnitsModal
				product={activeProduct}
				isOpen={viewDisc.isOpen}
				onClose={viewDisc.onClose}
			/>
			<ProductEditModal
				product={activeProduct}
				isOpen={editDisc.isOpen}
				onClose={editDisc.onClose}
				onSave={onEditSave}
			/>
		</Box>
	);
}
